/**
 * The stats window: the last `days` venue-timezone days (today included), or the
 * calendar days `from`–`to` (`YYYY-MM-DD`) inclusive.
 */
export type StatsRange = { days: number } | { from: string; to: string };

/** `range` as `/messaging/stats` query parameters. */
export const statsRangeParams = (range: StatsRange): Record<string, string> =>
  'days' in range
    ? { days: String(range.days) }
    : { from: range.from, to: range.to };

export interface StaffSentCount {
  userId: string;
  name: string;
  sent: number;
}

export interface DailyEngagementStats {
  /** Venue-timezone calendar day, `YYYY-MM-DD`. */
  date: string;
  sent: number;
  recipients: number;
  received: number;
  failed: number;
  flow: number;
  campaign: number;
  other: number;
  /** `flow` split by `FlowSeries.key`; keys with no sends that day are absent. */
  flows: Record<string, number>;
}

/** `FlowSeries.key` for flow sends from Flows outside the busiest few. */
export const OTHER_FLOWS_KEY = 'other-flows';
/** `FlowSeries.key` for flow sends whose Flow is unknown (sent before Flows were recorded, or deleted). */
export const UNATTRIBUTED_FLOW_KEY = 'unattributed';

/** One series of the per-Flow split of flow-triggered SMS. */
export interface FlowSeries {
  /** The Flow's id, `OTHER_FLOWS_KEY`, or `UNATTRIBUTED_FLOW_KEY`; keys `DailyEngagementStats.flows`. */
  key: string;
  flowId: string | null;
  /** The Flow's title; null for the two bucket keys. */
  title: string | null;
  total: number;
}

export interface EngagementStats {
  days: number;
  timezone: string;
  since: string;
  until: string;
  /** ISO weekdays (Mon = 1) and [startHour, endHour) in the venue timezone. */
  businessHours: { weekdays: number[]; startHour: number; endHour: number };
  sent: {
    total: number;
    dailyAverage: number;
    peakDay: { date: string; sent: number } | null;
    uniqueRecipients: number;
    activeSenders: number;
    senders: number;
  };
  received: {
    total: number;
    afterHours: number;
    afterHoursShare: number | null;
  };
  sentPerReceived: number | null;
  failed: {
    total: number;
    rate: number | null;
    topErrors: { code: string; message: string; count: number }[];
  };
  unread: { total: number; oldestReceivedAt: string | null };
  mix: { staff: number; flow: number; campaign: number; other: number };
  /** Busiest Flows first, then `OTHER_FLOWS_KEY` and `UNATTRIBUTED_FLOW_KEY` when they have sends. */
  flowSeries: FlowSeries[];
  daily: DailyEngagementStats[];
  receivedByHour: { hour: number; received: number }[];
  receivedByWeekday: { weekday: number; received: number }[];
  users: StaffSentCount[];
}

export interface FirstTouchGroup {
  instances: number;
  touched: number;
  medianSeconds: number | null;
}

export interface FirstTouchStats {
  flow: { id: string; title: string };
  days: number;
  timezone: string;
  since: string;
  until: string;
  /** A staff send counts as the first touch only within this many days of the start. */
  touchWindowDays: number;
  businessHours: EngagementStats['businessHours'];
  instances: number;
  touched: number;
  /** Untouched, still inside the touch window. */
  waiting: number;
  oldestWaitingStartedAt: string | null;
  /** Untouched, touch window closed. */
  missed: number;
  medianSeconds: number | null;
  p90Seconds: number | null;
  targets: { label: string; seconds: number; count: number; share: number | null }[];
  buckets: { label: string; count: number }[];
  daily: ({ date: string } & FirstTouchGroup)[];
  startedInBusinessHours: FirstTouchGroup;
  startedAfterHours: FirstTouchGroup;
  users: {
    userId: string;
    name: string;
    firstTouches: number;
    medianSeconds: number | null;
  }[];
}